import { JobApplication } from '../../../../core/models/jobs/job-application.model';
import { UrlParam } from '../../../../core/models/urlParam.model';
import {
  getApiBaseUrl,
  getPaginationSize,
} from '../../../../core/config/runtimeConfig';
import { BaseService } from '../../../../core/services/base.service';
import { JobContent, JobResponse } from '../models';
import { JobMetrics } from '../models/jobMetrics.model';
import {
  KofiRequiredError,
  throwIfKofiRequired,
} from '../../support/kofi.service';

export type ResumeImproveJobStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

export type ResumeImprovementItem = {
  section: string;
  description: string;
  category?: string | null;
  rationale?: string | null;
};

export type ResumeRecommendationItem = {
  section?: string | null;
  recommendation: string;
  rationale?: string | null;
};

export type ResumeImproveProfile = {
  targetRole?: string | null;
  seniority?: string | null;
  careerTrack?: string | null;
  domain?: string | null;
};

export type AuthenticityFlag = {
  type: string;
  severity: string;
  excerpt?: string | null;
  why: string;
  questionForCandidate?: string | null;
};

export type AtsFinding = {
  category: string;
  severity: string;
  message: string;
  excerpt?: string | null;
  fix?: string | null;
};

export type AtsReport = {
  overallScore: number;
  formattingScore: number;
  readabilityScore: number;
  keywordScore?: number | null;
  keywordsEvaluated: boolean;
  disclaimer: string;
  findings: AtsFinding[];
  matchedKeywords: string[];
  missingKeywords: string[];
};

export type AuthenticityReport = {
  mode: string;
  disclaimer: string;
  flags: AuthenticityFlag[];
};

export type ResumeAnalysisStatus = {
  jobId: string;
  kind: string;
  status: ResumeImproveJobStatus;
  message: string;
  errorMessage?: string | null;
  atsReport?: AtsReport | null;
  authenticityReport?: AuthenticityReport | null;
};

export type ResumeImproveJobStatusResponse = {
  jobId: string;
  status: ResumeImproveJobStatus;
  message: string;
  errorMessage?: string | null;
  downloadReady: boolean;
  summary?: string | null;
  improvements?: ResumeImprovementItem[] | null;
  recommendations?: ResumeRecommendationItem[] | null;
  profile?: ResumeImproveProfile | null;
  authenticityWarnings?: AuthenticityFlag[] | null;
};

export type ResumeImproveResult = {
  blob: Blob;
  summary?: string | null;
  improvements: ResumeImprovementItem[];
  recommendations: ResumeRecommendationItem[];
  profile?: ResumeImproveProfile | null;
  authenticityWarnings: AuthenticityFlag[];
};

export type ResumeImproveProgress = {
  status: ResumeImproveJobStatus;
  message: string;
};

const RESUME_IMPROVE_POLL_INTERVAL_MS = 2000;
const RESUME_IMPROVE_MAX_WAIT_MS = 10 * 60 * 1000;
const JOB_FIT_POLL_INTERVAL_MS = 2000;
const JOB_FIT_MAX_WAIT_MS = 10 * 60 * 1000;

export type JobFitJobStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export type JobFitAlignment = 'STRONG' | 'MODERATE' | 'WEAK';

export type JobFitSkillsSource = 'STORED' | 'INFERRED';

export type JobFitResult = {
  alignment: JobFitAlignment;
  score: number;
  matched_skills: string[];
  missing_skills: string[];
  summary: string;
  skills_source?: JobFitSkillsSource;
};

export type JobFitCandidateSummary = {
  created: boolean;
  emailFound: boolean;
};

export type JobFitSubmitResponse = {
  jobId: string;
  status: JobFitJobStatus;
  message: string;
};

export type JobFitStatusResponse = {
  status: JobFitJobStatus;
  message: string;
  errorMessage?: string | null;
  fit?: JobFitResult;
  candidate?: JobFitCandidateSummary;
};

export type JobFitProgress = {
  status: JobFitJobStatus;
  message: string;
};

export class JobService {
  static async GetJobs(
    categories: string[],
    subcategory: string,
    page: number,
    q: string = ''
  ): Promise<JobResponse> {
    try {
      const params = new URLSearchParams();
      categories.forEach((category) => {
        if (category.trim()) {
          params.append('category', category.trim());
        }
      });
      if (subcategory) {
        params.set('subCategory', subcategory);
      }
      if (q) {
        params.set('q', q);
      }
      params.set('page', page.toString());
      params.set('size', getPaginationSize().toString());

      const queryString = params.toString();
      const url = `${getApiBaseUrl()}/public/jobs${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      if (response.status === 204) {
        throw new Error(`SHOW: No jobs to show.`);
      }

      let json = await response.json();

      return json as JobResponse;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async GetSubCategories(): Promise<string[]> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/sub-categories`);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as string[];
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async GetJob(id: string): Promise<JobContent> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}`);

      const response = await fetch(url);

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as JobContent;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async GetMetrics(id: string): Promise<JobMetrics> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}/metrics`);

      const response = await fetch(url);

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as JobMetrics;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async AddView(id: string): Promise<number> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}/views`);

      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as number;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async AddLike(id: string): Promise<number> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}/likes`);

      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as number;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async ValidateDisable(id: string): Promise<boolean> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}/likes/disable`);

      const response = await fetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        throw new Error(`Error: ${response.status}`);
      }

      let json = await response.json();

      return json as boolean;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async PersonalInfoExtraction(file: File): Promise<any> {
    try {
      const url = BaseService.BuildUrl(
        `/public/resumes/personal-info-extraction`
      );

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${response.status} - ${data.message}`);
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async RegistrationInfoExtraction(file: File): Promise<any> {
    try {
      const url = BaseService.BuildUrl(
        `/public/resumes/registration-info-extraction`
      );

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${response.status} - ${data.message}`);
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async EasyApply(
    id: string,
    application: JobApplication,
    file: File
  ): Promise<JobApplication> {
    try {
      const url = BaseService.BuildUrl(`/public/jobs/${id}/easy-apply`);

      const formData = new FormData();
      formData.append('file', file);

      const request = JSON.stringify({
        firstname: application.firstname,
        lastname: application.lastname,
        email: application.email,
        phoneNumber: application.phoneNumber,
        websiteURL: application.websiteURL,
        whatsappNotification: application.whatsappNotifiable,
        emailNotification: application.emailNotifiable,
        currency: application.currency,
        expectedSalary: application.expectedSalary,
      });

      formData.append('request', request);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      let data = await response.json();

      if (!response.ok) {
        if ([401, 404].includes(response.status)) {
          throw new Error(`SHOW: El id ingresado no es válido.`);
        }

        if (
          data.message.includes(
            'com.josalero.posting.core.exception.ResourceAlreadyExistException'
          )
        ) {
          const match = data.message.match(/Candidate .*?position/);

          throw new Error(`${match}.`);
        }

        throw new Error(`${response.status} - ${data.message}`);
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message);
    }
  }
  //para para cargar el archivo
  static async ImproveResume(
    file: File,
    onProgress?: (progress: ResumeImproveProgress) => void
  ): Promise<ResumeImproveResult> {
    try {
      const submitResponse = await JobService.submitResumeImproveJob(file);
      onProgress?.({
        status: submitResponse.status,
        message: submitResponse.message,
      });

      const jobId = submitResponse.jobId;
      const startedAt = Date.now();

      while (Date.now() - startedAt < RESUME_IMPROVE_MAX_WAIT_MS) {
        await JobService.delay(RESUME_IMPROVE_POLL_INTERVAL_MS);
        const status = await JobService.getResumeImproveJobStatus(jobId);
        onProgress?.({
          status: status.status,
          message: status.message,
        });

        if (status.status === 'COMPLETED' && status.downloadReady) {
          const blob = await JobService.downloadResumeImproveJob(jobId);
          return {
            blob,
            summary: status.summary,
            improvements: status.improvements ?? [],
            recommendations: status.recommendations ?? [],
            profile: status.profile ?? null,
            authenticityWarnings: status.authenticityWarnings ?? [],
          };
        }

        if (status.status === 'FAILED') {
          throw new Error(
            status.errorMessage ??
              status.message ??
              'Resume processing failed. Please try again.'
          );
        }
      }

      throw new Error(
        'Resume processing is taking longer than expected. Please try again.'
      );
    } catch (error: unknown) {
      if (error instanceof KofiRequiredError) {
        throw error;
      }
      throw new Error(
        error instanceof Error ? error.message : 'Resume improvement failed'
      );
    }
  }

  static async CheckAts(
    file: File,
    options: { jobId?: string; jobDescription?: string },
    onProgress?: (progress: ResumeImproveProgress) => void
  ): Promise<AtsReport> {
    const status = await JobService.runAnalysis(
      '/public/resumes/ats-check/async',
      file,
      null,
      options,
      onProgress
    );
    if (!status.atsReport) {
      throw new Error('The ATS report was empty. Please try again.');
    }
    return status.atsReport;
  }

  static async CheckAuthenticity(
    file: File,
    originalFile: File | null,
    onProgress?: (progress: ResumeImproveProgress) => void
  ): Promise<AuthenticityReport> {
    const status = await JobService.runAnalysis(
      '/public/resumes/authenticity/async',
      file,
      originalFile,
      {},
      onProgress
    );
    if (!status.authenticityReport) {
      throw new Error('The authenticity report was empty. Please try again.');
    }
    return status.authenticityReport;
  }

  private static async runAnalysis(
    path: string,
    file: File,
    originalFile: File | null,
    options: { jobId?: string; jobDescription?: string },
    onProgress?: (progress: ResumeImproveProgress) => void
  ): Promise<ResumeAnalysisStatus> {
    const submit = await JobService.submitAnalysis(
      path,
      file,
      originalFile,
      options
    );
    onProgress?.({ status: submit.status, message: submit.message });
    const startedAt = Date.now();
    while (Date.now() - startedAt < RESUME_IMPROVE_MAX_WAIT_MS) {
      await JobService.delay(RESUME_IMPROVE_POLL_INTERVAL_MS);
      const status = await JobService.getAnalysisStatus(submit.jobId);
      onProgress?.({ status: status.status, message: status.message });
      if (status.status === 'COMPLETED') {
        return status;
      }
      if (status.status === 'FAILED') {
        throw new Error(
          status.errorMessage ??
            status.message ??
            'Resume check failed. Please try again.'
        );
      }
    }
    throw new Error(
      'The resume check is taking longer than expected. Please try again.'
    );
  }

  private static async submitAnalysis(
    path: string,
    file: File,
    originalFile: File | null,
    options: { jobId?: string; jobDescription?: string }
  ): Promise<{
    jobId: string;
    status: ResumeImproveJobStatus;
    message: string;
  }> {
    const params = new URLSearchParams();
    if (options.jobId) {
      params.set('jobId', options.jobId);
    }
    if (options.jobDescription) {
      params.set('jobDescription', options.jobDescription);
    }
    const query = params.toString();
    const url = BaseService.BuildUrl(query ? `${path}?${query}` : path);
    const formData = new FormData();
    formData.append('file', file);
    if (originalFile) {
      formData.append('originalFile', originalFile);
    }
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    await throwIfKofiRequired(response);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${response.status} - ${errorText}`);
    }
    return response.json();
  }

  private static async getAnalysisStatus(
    jobId: string
  ): Promise<ResumeAnalysisStatus> {
    const url = BaseService.BuildUrl(`/public/resumes/analysis/jobs/${jobId}`);
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${response.status} - ${errorText}`);
    }
    return response.json();
  }

  private static delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  static async submitResumeImproveJob(file: File): Promise<{
    jobId: string;
    status: ResumeImproveJobStatus;
    message: string;
  }> {
    const url = BaseService.BuildUrl('/public/resumes/improve/async');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    await throwIfKofiRequired(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${response.status} - ${errorText}`);
    }

    return response.json();
  }

  static async getResumeImproveJobStatus(
    jobId: string
  ): Promise<ResumeImproveJobStatusResponse> {
    const url = BaseService.BuildUrl(`/public/resumes/improve/jobs/${jobId}`);
    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${response.status} - ${errorText}`);
    }

    return response.json();
  }

  static async downloadResumeImproveJob(jobId: string): Promise<Blob> {
    const url = BaseService.BuildUrl(
      `/public/resumes/improve/jobs/${jobId}/download`
    );
    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${response.status} - ${errorText}`);
    }

    return response.blob();
  }

  static async runJobFit(
    postingJobId: string,
    file: File,
    onProgress?: (progress: JobFitProgress) => void
  ): Promise<JobFitStatusResponse> {
    onProgress?.({
      status: 'QUEUED',
      message: 'Uploading your resume…',
    });

    const submitResponse = await JobService.submitJobFit(postingJobId, file);
    onProgress?.({
      status: submitResponse.status,
      message: submitResponse.message,
    });

    try {
      return await JobService.waitForJobFitViaSse(
        postingJobId,
        submitResponse.jobId,
        onProgress
      );
    } catch {
      return JobService.pollJobFitUntilComplete(
        postingJobId,
        submitResponse.jobId,
        onProgress
      );
    }
  }

  static async submitJobFit(
    postingJobId: string,
    file: File
  ): Promise<JobFitSubmitResponse> {
    const url = BaseService.BuildUrl(
      `/public/jobs/${postingJobId}/resume-fit/async`
    );
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    await throwIfKofiRequired(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(JobService.parseApiError(response.status, errorText));
    }

    return response.json();
  }

  static async getJobFitStatus(
    postingJobId: string,
    asyncJobId: string
  ): Promise<JobFitStatusResponse> {
    const url = BaseService.BuildUrl(
      `/public/jobs/${postingJobId}/resume-fit/jobs/${asyncJobId}`
    );
    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(JobService.parseApiError(response.status, errorText));
    }

    return response.json();
  }

  private static waitForJobFitViaSse(
    postingJobId: string,
    asyncJobId: string,
    onProgress?: (progress: JobFitProgress) => void
  ): Promise<JobFitStatusResponse> {
    return new Promise((resolve, reject) => {
      const url = BaseService.BuildUrl(
        `/public/jobs/${postingJobId}/resume-fit/jobs/${asyncJobId}/events`
      );
      let settled = false;

      const finish = (action: () => void) => {
        if (!settled) {
          settled = true;
          action();
        }
      };

      const source = new EventSource(url);

      source.addEventListener('status', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as {
            status: JobFitJobStatus;
            message: string;
          };
          onProgress?.({ status: data.status, message: data.message });
        } catch {
          // Ignore malformed SSE payloads and keep listening.
        }
      });

      source.addEventListener('completed', (event) => {
        source.close();
        finish(() => {
          try {
            resolve(
              JSON.parse((event as MessageEvent).data) as JobFitStatusResponse
            );
          } catch {
            reject(
              new Error('Fit check completed but the response was invalid.')
            );
          }
        });
      });

      source.addEventListener('failed', (event) => {
        source.close();
        finish(() => {
          try {
            const data = JSON.parse(
              (event as MessageEvent).data
            ) as JobFitStatusResponse;
            reject(
              new Error(
                data.errorMessage ??
                  data.message ??
                  'Resume analysis failed. Please try again.'
              )
            );
          } catch {
            reject(new Error('Resume analysis failed. Please try again.'));
          }
        });
      });

      source.onerror = () => {
        source.close();
        finish(() =>
          reject(new Error('SSE connection failed. Falling back to polling.'))
        );
      };
    });
  }

  private static async pollJobFitUntilComplete(
    postingJobId: string,
    asyncJobId: string,
    onProgress?: (progress: JobFitProgress) => void
  ): Promise<JobFitStatusResponse> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < JOB_FIT_MAX_WAIT_MS) {
      await JobService.delay(JOB_FIT_POLL_INTERVAL_MS);
      const status = await JobService.getJobFitStatus(postingJobId, asyncJobId);
      onProgress?.({
        status: status.status,
        message: status.message,
      });

      if (status.status === 'COMPLETED') {
        return status;
      }

      if (status.status === 'FAILED') {
        throw new Error(
          status.errorMessage ??
            status.message ??
            'Resume analysis failed. Please try again.'
        );
      }
    }

    throw new Error(
      'Fit check is taking longer than expected. Please try again.'
    );
  }

  private static parseApiError(status: number, errorText: string): string {
    try {
      const data = JSON.parse(errorText) as { message?: string };
      if (data.message) {
        return `${status} - ${data.message}`;
      }
    } catch {
      // Use raw text below.
    }
    return `${status} - ${errorText}`;
  }
}
